<template lang="pug">
  ValidationObserver(:ref="model.entity" slim)
    v-row()
      v-col(cols="12" class="mt-1 mb-1")
        t-orm-fields(
          v-bind.sync="editedItem"
          :items="fields"
          :tabs="model.ormTabs"
          :context="context"
          :key="key"
        )
    v-row(class="mb-4")
      v-col(cols="12")
        t-orm-buttons(:items="buttons" :context="context" :classes="['text-center']")
</template>

<script>
import TOrmButtons from '~/components/templates/orm/t-orm-buttons'
import TOrmFields from '~/components/templates/orm/t-orm-fields'
import validate from '~/mixins/validation/validate'
import filterOrmObject from '~/mixins/orm/filterOrmObject'
import checkPropCtx from '~/mixins/methods/checkPropCtx'
import taxAuth from '~/mixins/pages/tax-auth'
import ELink from '~/components/elements/links/e-link'
import Taxes from '~/modules/tax-rates/models/Taxes'
import TOrmLabel from '~/components/templates/orm/_includes/t-orm-label'
import actions from '~/modules/goods/mixins/actions'
import scrollToFailedValidation from '~/mixins/methods/scrollToFailedValidation'

export default {
  components: { TOrmButtons, TOrmFields, ELink, TOrmLabel },
  mixins: [validate, filterOrmObject, checkPropCtx, taxAuth, actions, scrollToFailedValidation],
  props: {
    context: {
      type: String,
      default: 'create'
    },
    selectedItem: {
      type: Object,
      default: null
    },
    item: {
      type: Object,
      default: null
    },
    isDialogPart: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    model: Taxes,
    editedItem: {
      name: null,
      type: null,
      percent: null,
      taxationType: null
    },
    itemLoading: false,
    key: 0
  }),
  computed: {
    isUpdate () {
      return this.context === 'update'
    },
    fields () {
      return [
        {
          model: 'name',
          component: 'v-text-field',
          provider: {
            vid: 'name',
            name: 'Tax name',
            rules: 'required'
          },
          attrs: {
            label: 'Tax name',
            outlined: true
          }
        },
        {
          model: 'type',
          component: 'v-text-field',
          provider: {
            vid: 'type',
            name: 'Tax type',
            rules: 'required'
          },
          attrs: {
            label: 'Tax type',
            outlined: true,
            visible: false
          },
          fieldVal: () => 'VAT'
        },
        {
          model: 'percent',
          component: 'v-text-field',
          provider: {
            vid: 'percent',
            name: 'Tax percent',
            rules: 'required|integer_or_decimal|min_value:0'
          },
          cast: val => this.numFieldCast(val, false),
          attrs: {
            label: 'Tax percent',
            outlined: true,
            type: 'number',
            min: 0
          }
        },
        {
          model: 'taxationType',
          component: 'v-select',
          provider: {
            vid: 'taxationType',
            name: 'Taxation type',
            rules: 'required'
          },
          attrs: {
            label: 'Taxation type',
            outlined: true
          },
          items: () => [
            {
              text: this.$t('SNR based on a simplified declaration'),
              value: 'STS'
            },
            {
              text: this.$t('General taxation regime'),
              value: 'RTS'
            },
            {
              text: this.$t('SNR for peasants or farms'),
              value: 'TRFF'
            },
            {
              text: this.$t('SNR based on patent'),
              value: 'TRBP'
            }
          ]
        }
      ]
    },
    buttons () {
      const text = this.$isCreateCtx(this.context) ? 'Create' : 'Save'
      return [
        {
          text,
          contexts: this.$config.contexts.only('c.u'),
          loading: this.itemLoading || this._.get(this.iitDialog, 'options.loading', false),
          attrs: {
            depressed: true,
            class: 'main-button'
          },
          call: this.save
        }
      ]
    }
  },
  watch: {
    'editedItem.noVat' (val) {
      if (val) {
        this.$set(this.editedItem, 'rate', 0)
        this.$set(this.editedItem, 'extraRate', 0)
        this.key += 1
      }
    }
  },
  created () {
    if (this.isUpdate) {
      this._.each(this.item, (value, key) => {
        if (Object.keys(this.editedItem).includes(key)) {
          let val = value
          if (key === 'percent') {
            val = (value / 1000).toFixed(2)
          }
          this.$set(this.editedItem, key, val)
        }
      })
    }
  },
  methods: {
    numFieldCast (value, int = true) {
      const parsedVal = int ? parseInt(value) : parseFloat(value)
      return typeof parsedVal === 'number' && !isNaN(parsedVal) ? parsedVal : null
    },
    changeEntityType (type) {
      this.$set(this.productItem, 'type', type)
    },
    close () {
      this.$emit(['dialog', 'close'].join(':'), null)
    },
    async save () {
      if (!await this.validate()) {
        this.scrollToFailedValidation(this.$refs[this.model.entity]?.$el)
        return false
      }

      try {
        this.itemLoading = true

        const payload = this.filterOrmObject(this.editedItem)
        payload.percent = parseInt(((payload.percent || 0) * 1000).toFixed(0))

        this.$isCreateCtx(this.context)
          ? await this.model.api()[this.context](payload, false)
          : await this.model.api()[this.context](this.item, payload, false)

        if (this.$isCreateCtx(this.context)) {
          this.$notification.success(this.$t('Tax rate was created'))
        } else {
          this.$notification.success(this.$t('Tax rate was updated'))
        }

        this.close()
      } catch (e) {
        this.$handlers.error(e, this.$refs[this.model.entity])
        this.scrollToFailedValidation(this.$refs[this.model.entity]?.$el)
      } finally {
        this.itemLoading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.hint {
  font-size: 0.75rem;
  line-height: 1.2;
}
.max-width {
  max-width: 27%;
}
</style>
